// EQUIPO — port al manager de la página "Usuarios y roles" (agenda dental → letsmove →
// aquí, 2026-07-04). Módulo core `users_admin`: invitar (correo brandeado), activar/
// desactivar, cambiar rol, reenviar invitación/reset y PERMISOS POR MÓDULO (overrides
// personales; solo el dueño). Un Gerente ve y opera (invitar empleados); solo el Dueño
// gestiona dueños, permisos y "Ver como".
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Mail, KeyRound, Shield, X, Check, Loader2, Users, SlidersHorizontal, Eye, Crown, MapPin } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Layout from '../../../components/layout/Layout';
import { useAuth } from '../../../context/AuthContext';
import { isOwner as isOwnerUser } from '../../../utils/modulePermissions';
import { isPro as checkIsPro } from '../../../utils/planPermissions';
import {
  listOperators, inviteOperator, patchOperator, resendInvite, sendReset,
  getPermissions, savePermissions, impersonate,
  Operator, UserRole, ModulePermission, PermLevel,
} from '../../../api/teamApi';

const ROLE_LABEL: Record<UserRole, string> = { owner: 'Dueño(a)', manager: 'Gerente', staff: 'Empleado' };
const ROLE_CLS: Record<UserRole, string> = {
  owner:   'bg-accent/15 text-accent',
  manager: 'bg-accent/10 text-accent',
  staff:   'bg-surface-2 text-content-2',
};
// Qué puede y qué no cada rol, en el lenguaje del negocio. Refleja los defaults reales del
// motor (role_module_defaults) + las pantallas que el panel esconde por nivel (PATH_MIN_LEVEL).
// Se muestra en las tarjetas de Equipo y, al invitar, debajo del rol elegido.
const ROLE_CAN: Record<UserRole, { puede: string; noPuede: string }> = {
  staff: {
    puede: 'registrar clientes, dar y canjear puntos, canjear recompensas y gift cards, ver clientes y visitas, y felicitar cumpleaños.',
    noPuede: 'cambiar cómo se premia (recompensas, puntos, membresías), ni los mensajes automáticos, campañas, WhatsApp, Mi Negocio, Suscripción o el Equipo. Tampoco ajusta saldos a mano.',
  },
  manager: {
    puede: 'todo lo del Empleado y además ajustar saldos, editar recompensas, gift cards, cumpleaños, mensajes automáticos y WhatsApp; ve Analíticas e invita Empleados.',
    noPuede: 'ver la Suscripción ni cambiar permisos del equipo.',
  },
  owner: {
    puede: 'todo: Mi Negocio completo, Suscripción y el control del Equipo (invita Dueños, ajusta permisos por persona y usa «Ver como»).',
    noPuede: 'nada; es el acceso total.',
  },
};
const LEVEL_LABEL: Record<PermLevel, string> = {
  none: 'Sin acceso', view: 'Solo ver', operate: 'Operar', manage: 'Administrar',
};

// Módulos que el MANAGER realmente usa, con nombre y pantallas en el lenguaje del panel.
// El backend devuelve TODOS los módulos activos del tenant (incluye core/cross de otras
// verticales: finanzas POS, caja, empleados-asistencia, administrar módulos…) — aquí se
// filtra a lo que tiene pantalla en este panel para que la matriz tenga sentido.
const MANAGER_MODULES: Record<string, { label: string; screens: string }> = {
  contacts:    { label: 'Clientes',              screens: 'Lista de Clientes, Registrar Cliente, Últimos Registros, Mejores, Inactivos, Insatisfechos, Etiquetas' },
  loyalty:     { label: 'Lealtad',               screens: 'Últimas Visitas, Gestionar Puntos, Recompensas, Canjear Recompensas, Referidos' },
  birthdays:   { label: 'Cumpleaños',            screens: 'Cumpleaños' },
  giftcards:   { label: 'Gift Cards',            screens: 'Gift Cards (Enviar y Ajustes)' },
  campaigns:   { label: 'Campañas',              screens: 'Campañas' },
  templates:   { label: 'Mensajes y WhatsApp',   screens: 'Mensajes automáticos (bienvenida, visita, referidos, opinión, saldo bajo, reseñas, membresías), WhatsApp del negocio' },
  reports:     { label: 'Analíticas',            screens: 'Analíticas' },
  billing:     { label: 'Suscripción',           screens: 'Suscripción' },
  settings:    { label: 'Mi Negocio',            screens: 'Mi Negocio (incluye los términos legales)' },
  users_admin: { label: 'Equipo',                screens: 'Equipo (usuarios, roles y permisos)' },
};
const MODULE_ORDER = Object.keys(MANAGER_MODULES);

export default function TeamPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isOwner = isOwnerUser(currentUser);
  const selfId = Number(currentUser?.uid);
  // Invitar equipo = función PRO (decisión Carlos 4-jul). Free ve el botón dorado → Suscripción.
  const hasProAccess = checkIsPro(currentUser?.subscription);
  const [ops, setOps] = useState<Operator[] | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [permsFor, setPermsFor] = useState<Operator | null>(null);
  const [sucursalesFor, setSucursalesFor] = useState<Operator | null>(null);
  // Sucursales (10-oct-2026, Bramarket): con 2+ el dueño limita a cada persona a las suyas; sin marcar = todas.
  // Un empleado limitado solo ve y registra en las suyas (kiosco y servidor lo exigen).
  const sucursales = (currentUser?.branches || []).filter((b) => b.isActive !== false)
    .map((b) => ({ id: Number(b.id), name: b.name }));
  const variasSucursales = sucursales.length >= 2;
  const nombresSucursales = (ids: number[]) => {
    const n = sucursales.filter((b) => ids.includes(b.id)).map((b) => b.name);
    return n.length && n.length < sucursales.length ? n.join(', ') : 'Todas las sucursales';
  };

  const load = () => listOperators().then((r) => setOps(r.users))
    .catch((e) => toast.error((e as Error).message));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function withBusy(id: number, fn: () => Promise<unknown>, okMsg?: string) {
    setBusyId(id);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  }

  const toggleActive = (o: Operator) => {
    if (o.id === selfId) { toast.error('No puedes desactivar tu propia cuenta.'); return; }
    withBusy(o.id, () => patchOperator(o.id, { is_active: o.is_active ? 0 : 1 }),
      o.is_active ? 'Cuenta desactivada' : 'Cuenta activada');
  };
  const changeRole = (o: Operator, role: UserRole) =>
    withBusy(o.id, () => patchOperator(o.id, { role }), 'Rol actualizado');

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div>
            <h1 className="text-2xl font-bold text-content flex items-center">
              <Users className="h-6 w-6 mr-2 text-accent" /> Equipo
            </h1>
            <p className="mt-1 text-sm text-content-2">Tu equipo con acceso al panel. Invita, define roles y ajusta permisos por persona.</p>
          </div>
          {hasProAccess ? (
            <button onClick={() => setShowInvite(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-accent-solid px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-accent-solid/90">
              <UserPlus size={16} /> Invitar usuario
            </button>
          ) : (
            <button onClick={() => navigate('/dashboard/subscription')}
              title="Invitar a tu equipo es una función PRO"
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-yellow-500 to-yellow-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:from-yellow-600 hover:to-yellow-700">
              <Crown size={16} /> Invitar usuario (PRO)
            </button>
          )}
        </div>

        <div className="animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          {ops === null ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-accent" /></div>
          ) : ops.length === 0 ? (
            <p className="py-16 text-center text-content-3">Aún no hay usuarios.</p>
          ) : (
            <div className="space-y-2.5">
              {ops.map((o) => {
                const pending = o.is_active === 1 && o.has_password === 0;
                const isSelf = o.id === selfId;
                const canTouch = isOwner || o.role !== 'owner'; // solo dueño gestiona dueños
                return (
                  <div key={o.id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-surface p-3.5 shadow-sm">
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent/15 text-base font-bold text-accent">
                      {o.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-semibold text-content">{o.name}</span>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${ROLE_CLS[o.role]}`}>{ROLE_LABEL[o.role]}</span>
                        {isSelf && <span className="shrink-0 text-[11px] text-content-3">(tú)</span>}
                      </div>
                      <div className="truncate text-xs text-content-3">{o.email || 'sin correo'}</div>
                      {variasSucursales && o.role !== 'owner' && (
                        <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-content-3" data-testid={`sucursales-${o.id}`}>
                          <MapPin size={11} className="shrink-0" /> {nombresSucursales(o.branch_ids || [])}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0">
                      {o.is_active === 0 ? (
                        <span className="rounded-full bg-surface-2 px-2.5 py-1 text-xs font-medium text-content-3">Inactivo</span>
                      ) : pending ? (
                        <span className="rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-500">Pendiente</span>
                      ) : (
                        <span className="rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-500">Activo</span>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <select value={o.role} disabled={!canTouch || isSelf || busyId === o.id}
                        onChange={(e) => changeRole(o, e.target.value as UserRole)} title="Cambiar rol"
                        className="rounded-lg border border-line bg-surface-2 px-2 py-1.5 text-xs text-content-2 disabled:opacity-50">
                        <option value="owner">Dueño(a)</option>
                        <option value="manager">Gerente</option>
                        <option value="staff">Empleado</option>
                      </select>
                      {o.email && canTouch && (
                        pending ? (
                          <button onClick={() => withBusy(o.id, () => resendInvite(o.id), 'Invitación reenviada')}
                            disabled={busyId === o.id} title="Reenviar invitación"
                            className="rounded-lg border border-line p-2 text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
                            <Mail size={15} />
                          </button>
                        ) : (
                          <button onClick={() => withBusy(o.id, () => sendReset(o.id), 'Correo de reset enviado')}
                            disabled={busyId === o.id} title="Enviar restablecer contraseña"
                            className="rounded-lg border border-line p-2 text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
                            <KeyRound size={15} />
                          </button>
                        )
                      )}
                      {variasSucursales && canTouch && o.role !== 'owner' && (
                        <button onClick={() => setSucursalesFor(o)} disabled={busyId === o.id} title="Sucursales"
                          data-testid={`editar-sucursales-${o.id}`}
                          className="rounded-lg border border-line p-2 text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
                          <MapPin size={15} />
                        </button>
                      )}
                      {isOwner && canTouch && (
                        <button onClick={() => setPermsFor(o)} disabled={busyId === o.id} title="Permisos por módulo"
                          className="rounded-lg border border-line p-2 text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
                          <SlidersHorizontal size={15} />
                        </button>
                      )}
                      {/* Ver como: abre el panel con los ojos de esta persona (solo owner, nunca sobre owners) */}
                      {isOwner && o.role !== 'owner' && !isSelf && o.is_active === 1 && (
                        <button
                          onClick={() => withBusy(o.id, async () => { await impersonate(o.id); window.location.href = '/dashboard'; })}
                          disabled={busyId === o.id} title={`Ver el panel como ${o.name}`}
                          className="rounded-lg border border-line p-2 text-content-2 transition hover:bg-surface-2 disabled:opacity-50">
                          <Eye size={15} />
                        </button>
                      )}
                      {canTouch && !isSelf && (
                        <button onClick={() => toggleActive(o)} disabled={busyId === o.id}
                          title={o.is_active ? 'Desactivar' : 'Activar'}
                          className={`rounded-lg p-2 disabled:opacity-50 ${o.is_active ? 'text-rose-500 hover:bg-rose-500/10' : 'text-emerald-500 hover:bg-emerald-500/10'}`}>
                          {o.is_active ? <X size={15} /> : <Check size={15} />}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <p className="mt-5 flex items-center gap-1.5 text-xs text-content-3">
            <Shield size={13} /> "Pendiente" = invitado que aún no fija su contraseña. Solo un Dueño gestiona Dueños y permisos.
          </p>

          {/* Qué puede hacer cada rol (refleja los defaults reales del motor de permisos;
              los ajustes por persona desde el botón de controles pueden cambiarlo). */}
          <div className="mt-6 grid gap-3 sm:grid-cols-3" data-testid="roles-cards">
            {(['staff', 'manager', 'owner'] as UserRole[]).map((r) => (
              <div key={r} className="rounded-2xl border border-line bg-surface p-4">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${ROLE_CLS[r]}`}>{ROLE_LABEL[r]}</span>
                <p className="mt-2 text-xs text-content-2 leading-relaxed">
                  <b className="text-emerald-700 dark:text-emerald-400">Puede:</b> {ROLE_CAN[r].puede}
                </p>
                <p className="mt-1.5 text-xs text-content-2 leading-relaxed">
                  <b className="text-red-600 dark:text-red-400">No puede:</b> {ROLE_CAN[r].noPuede}
                </p>
              </div>
            ))}
          </div>
        </div>

        {showInvite && <InviteModal sucursales={variasSucursales ? sucursales : []} onClose={() => setShowInvite(false)} onDone={() => { setShowInvite(false); load(); }} />}
        {sucursalesFor && (
          <SucursalesModal op={sucursalesFor} sucursales={sucursales} onClose={() => setSucursalesFor(null)}
            onSave={(ids) => withBusy(sucursalesFor.id, () => patchOperator(sucursalesFor.id, { branch_ids: ids }), 'Sucursales actualizadas')
              .then(() => setSucursalesFor(null))} />
        )}
        {permsFor && <PermissionsModal op={permsFor} selfId={selfId} onClose={() => setPermsFor(null)} />}
      </div>
    </Layout>
  );
}

// ── Modal de invitación (portal: overlays fuera del flujo del layout) ──
type SucursalOpc = { id: number; name: string };

// Casillas de sucursales (alta y edición). Sin marcar ninguna = todas.
function ElegirSucursales({ sucursales, value, onChange }: { sucursales: SucursalOpc[]; value: number[]; onChange: (v: number[]) => void }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-content-2">Sucursales</label>
      <div className="space-y-1.5 rounded-lg border border-line bg-surface-2 p-2.5">
        {sucursales.map((b) => (
          <label key={b.id} className="flex cursor-pointer items-center gap-2 text-sm text-content">
            <input type="checkbox" checked={value.includes(b.id)} data-testid={`sucursal-${b.id}`}
              onChange={(e) => onChange(e.target.checked ? [...value, b.id] : value.filter((x) => x !== b.id))} />
            {b.name}
          </label>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-content-3">
        Solo verá y registrará en las sucursales marcadas. Sin marcar ninguna = todas.
      </p>
    </div>
  );
}

function SucursalesModal({ op, sucursales, onClose, onSave }: { op: Operator; sucursales: SucursalOpc[]; onClose: () => void; onSave: (ids: number[]) => Promise<unknown> }) {
  const [sel, setSel] = useState<number[]>(op.branch_ids || []);
  const [saving, setSaving] = useState(false);
  return createPortal(
    <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="modal-sucursales"
        className="modal-sheet w-full rounded-t-2xl border border-line bg-surface p-5 shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-content"><MapPin size={18} className="text-accent" /> Sucursales de {op.name}</h2>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>
        <ElegirSucursales sucursales={sucursales} value={sel} onChange={setSel} />
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm text-content-2 hover:bg-surface-2">Cancelar</button>
          <button type="button" disabled={saving} data-testid="guardar-sucursales"
            onClick={async () => { setSaving(true); try { await onSave(sel); } finally { setSaving(false); } }}
            className="inline-flex items-center gap-2 rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-white hover:bg-accent-solid/90 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Guardar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function InviteModal({ sucursales, onClose, onDone }: { sucursales: SucursalOpc[]; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('staff');
  const [branchIds, setBranchIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error('Nombre y correo son obligatorios.'); return; }
    setSaving(true);
    try {
      const r = await inviteOperator({ name: name.trim(), email: email.trim(), role,
        ...(sucursales.length && role !== 'owner' ? { branch_ids: branchIds } : {}) });
      toast.success(r.invited ? 'Invitación enviada por correo.' : 'Usuario creado (sin correo de invitación).');
      onDone();
    } catch (err) { toast.error((err as Error).message); }
    finally { setSaving(false); }
  };

  const input = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-sm text-content outline-none focus:border-accent/50';
  return createPortal(
    <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="modal-sheet w-full rounded-t-2xl border border-line bg-surface p-5 shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-content"><UserPlus size={18} className="text-accent" /> Invitar usuario</h2>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-content-2">Nombre</label>
            <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre y apellido" autoFocus />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-content-2">Correo</label>
            <input className={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@ejemplo.com" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-content-2">Rol</label>
            <select className={input} value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
              <option value="staff">Empleado</option>
              <option value="manager">Gerente</option>
              <option value="owner">Dueño(a)</option>
            </select>
            <div className="mt-2 rounded-lg border border-line bg-surface-2/60 px-3 py-2 text-[11px] leading-relaxed text-content-2" data-testid="rol-explica">
              <p><b className="text-emerald-700 dark:text-emerald-400">Puede:</b> {ROLE_CAN[role].puede}</p>
              <p className="mt-1"><b className="text-red-600 dark:text-red-400">No puede:</b> {ROLE_CAN[role].noPuede}</p>
            </div>
          </div>
          {sucursales.length > 0 && role !== 'owner' && (
            <ElegirSucursales sucursales={sucursales} value={branchIds} onChange={setBranchIds} />
          )}
        </div>
        <p className="mt-3 text-xs text-content-3">Se le enviará un correo para que fije su contraseña y active su cuenta.</p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm text-content-2 hover:bg-surface-2">Cancelar</button>
          <button type="submit" disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-white hover:bg-accent-solid/90 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Enviar invitación
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

// ── Modal de permisos por módulo (portal) ──
function PermissionsModal({ op, selfId, onClose }: { op: Operator; selfId: number; onClose: () => void }) {
  const [mods, setMods] = useState<ModulePermission[] | null>(null);
  const [dirty, setDirty] = useState<Record<string, PermLevel | null>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getPermissions(op.id)
      .then((r) => setMods(
        r.modules
          .filter((m) => m.key in MANAGER_MODULES)
          .sort((a, b) => MODULE_ORDER.indexOf(a.key) - MODULE_ORDER.indexOf(b.key))
      ))
      .catch((e) => { toast.error((e as Error).message); onClose(); });
  }, [op.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentOverride = (m: ModulePermission): PermLevel | null =>
    m.key in dirty ? dirty[m.key] : m.override;

  const setLevel = (m: ModulePermission, v: string) =>
    setDirty((prev) => ({ ...prev, [m.key]: v === 'inherit' ? null : (v as PermLevel) }));

  const save = async () => {
    if (!Object.keys(dirty).length) { onClose(); return; }
    setSaving(true);
    try {
      await savePermissions(op.id, dirty);
      toast.success('Permisos actualizados');
      onClose();
    } catch (e) { toast.error((e as Error).message); setSaving(false); }
  };

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="modal-sheet flex max-h-[85vh] w-full flex-col rounded-t-2xl border border-line bg-surface p-5 shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-content">
            <SlidersHorizontal size={18} className="text-accent" /> Permisos de {op.name}
          </h2>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>
        <p className="mb-3 text-xs text-content-3">
          Rol: <b>{ROLE_LABEL[op.role]}</b>. "Heredado" sigue lo que da su rol; cambiarlo crea un ajuste solo para esta persona.
        </p>

        {mods === null ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-accent" /></div>
        ) : (
          <div className="-mr-2 flex-1 space-y-1 overflow-y-auto pr-2">
            {mods.map((m) => {
              const ov = currentOverride(m);
              const lockSelf = m.key === 'users_admin' && op.id === selfId; // anti-lockout (también en backend)
              return (
                <div key={m.key} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${ov !== null ? 'bg-accent/10' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-content">
                      {MANAGER_MODULES[m.key]?.label || m.name}
                      {ov !== null && <span className="ml-1.5 rounded-full bg-accent/15 px-1.5 py-0.5 text-[10px] font-semibold text-accent">ajustado</span>}
                    </p>
                    <p className="truncate text-[11px] text-content-3" title={MANAGER_MODULES[m.key]?.screens}>
                      {MANAGER_MODULES[m.key]?.screens}
                    </p>
                    <p className="text-[11px] text-content-3">Por rol: {LEVEL_LABEL[m.inherited]}</p>
                  </div>
                  <select value={ov === null ? 'inherit' : ov} disabled={lockSelf}
                    onChange={(e) => setLevel(m, e.target.value)}
                    className="shrink-0 rounded-lg border border-line bg-surface-2 px-2 py-1.5 text-xs text-content-2 disabled:opacity-50">
                    <option value="inherit">Heredado ({LEVEL_LABEL[m.inherited]})</option>
                    <option value="none">Sin acceso</option>
                    <option value="view">Solo ver</option>
                    <option value="operate">Operar</option>
                    <option value="manage">Administrar</option>
                  </select>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <p className="text-[11px] text-content-3">Los cambios aplican de inmediato; la persona verá su menú actualizado al recargar.</p>
          <div className="flex shrink-0 gap-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-line px-3 py-2 text-sm text-content-2 hover:bg-surface-2">Cancelar</button>
            <button type="button" onClick={save} disabled={saving || mods === null}
              className="inline-flex items-center gap-2 rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-white hover:bg-accent-solid/90 disabled:opacity-60">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Guardar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
